import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { SGetUssdResponses, SGetUssdStatistics } from "../../apis/app.service";
import LoadingSpinner from "../sharedcomponents/LoadingSpinner";
import AnalyticsDatePicker from "../Analytics/components/AnalyticsDatePicker";
import moment from "moment";
import dayjs from "dayjs";
import "./core/styles.scss";

function UssdPage() {
  const [currentPage, setCurrentPage] = useState(1);
  const [sessionId, setSessionId] = useState("");
  const [itemsPerPage, setItemsPerPage] = useState(50);

  // Date picker state
  const [dateType, setDateType] = useState("month");
  const [selectedDate, setSelectedDate] = useState<any>(dayjs());
  const [selectedDateFormatted, setSelectedDateFormatted] = useState(
    dayjs().format("YYYY-MM")
  );
  const [startDate, setStartDate] = useState(
    dayjs().startOf("month").format("YYYY-MM-DD")
  );
  const [endDate, setEndDate] = useState(
    dayjs().endOf("month").format("YYYY-MM-DD")
  );

  const handleDateChange = (start: string, end: string) => {
    setStartDate(start);
    setEndDate(end);
  };

  const {
    data: ussdData,
    isLoading,
    error,
  } = useQuery({
    queryKey: [
      "ussdResponses",
      currentPage,
      startDate,
      endDate,
      sessionId,
      itemsPerPage,
    ],
    queryFn: () =>
      SGetUssdResponses({
        page: currentPage,
        limit: itemsPerPage,
        startDate,
        endDate,
        sessionId,
      }),
  });

  const { data: statsData } = useQuery({
    queryKey: ["ussdStatistics", startDate, endDate],
    queryFn: () => SGetUssdStatistics({ startDate, endDate }),
  });

  const handleClearFilters = () => {
    setSessionId("");
    setCurrentPage(1);
    setDateType("month");
    setSelectedDate(dayjs());
    setSelectedDateFormatted(dayjs().format("YYYY-MM"));
    setStartDate(dayjs().startOf("month").format("YYYY-MM-DD"));
    setEndDate(dayjs().endOf("month").format("YYYY-MM-DD"));
  };

  const handlePageSizeChange = (newSize: number) => {
    setItemsPerPage(newSize);
    setCurrentPage(1);
  };

  const downloadCSV = () => {
    if (!ussdData?.data.responses || ussdData.data.responses.length === 0) {
      alert("No data to download");
      return;
    }

    // Create CSV headers
    const headers = [
      "#",
      "Session ID",
      "Language",
      "Institution",
      "Office",
      "Department",
      "Rating",
      "Waiting Time",
      "Reasons for Rating",
      "Gender",
      "Date",
    ];
    const csvContent = [
      headers.join(","),
      ...ussdData.data.responses.map((response: any, index: number) => {
        const row = [
          (currentPage - 1) * itemsPerPage + index + 1,
          response.sessionId || "-",
          response.language || "-",
          response.institution || "-",
          response.office || "-",
          response.department || "-",
          response.rating || "-",
          response.waitingTime || "-",
          `"${(response.reasonsRorRating || "-").replace(/"/g, '""')}"`,
          response.gender || "-",
          response.createdAt
            ? moment(response.createdAt).format("YYYY-MM-DD HH:mm")
            : "-",
        ];
        return row.join(",");
      }),
    ].join("\n");

    // Create blob and download
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute(
      "download",
      `USSD_Responses_${moment().format("YYYY-MM-DD")}.csv`
    );
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const downloadPDF = () => {
    if (!ussdData?.data.responses || ussdData.data.responses.length === 0) {
      alert("No data to download");
      return;
    }

    // Create a printable HTML table
    const printWindow = window.open("", "_blank");
    if (!printWindow) {
      alert("Please allow pop-ups to download PDF");
      return;
    }

    const htmlContent = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>USSD Responses - Data Export</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 20px; }
            h1 { color: #333; margin-bottom: 10px; }
            .info { color: #666; margin-bottom: 20px; }
            table { width: 100%; border-collapse: collapse; margin-top: 20px; font-size: 10px; }
            th, td { border: 1px solid #ddd; padding: 6px; text-align: left; }
            th { background-color: #1976d2; color: white; font-weight: bold; }
            tr:nth-child(even) { background-color: #f2f2f2; }
            @media print {
              body { padding: 10px; }
              table { page-break-inside: auto; }
              tr { page-break-inside: avoid; page-break-after: auto; }
            }
          </style>
        </head>
        <body>
          <h1>USSD Responses - Data Export</h1>
          <div class="info">
            <p>Total Responses: ${statsData?.data.total || 0}</p>
            <p>Export Date: ${moment().format("YYYY-MM-DD HH:mm")}</p>
            ${startDate ? `<p>Start Date: ${startDate}</p>` : ""}
            ${endDate ? `<p>End Date: ${endDate}</p>` : ""}
          </div>
          <table>
            <thead>
              <tr>
                <th>#</th>
                <th>Session ID</th>
                <th>Language</th>
                <th>Institution</th>
                <th>Office</th>
                <th>Department</th>
                <th>Rating</th>
                <th>Waiting Time</th>
                <th>Reasons for Rating</th>
                <th>Gender</th>
                <th>Date</th>
              </tr>
            </thead>
            <tbody>
              ${ussdData.data.responses
                .map(
                  (response: any, index: number) => `
                <tr>
                  <td>${(currentPage - 1) * itemsPerPage + index + 1}</td>
                  <td>${response.sessionId || "-"}</td>
                  <td>${response.language || "-"}</td>
                  <td>${response.institution || "-"}</td>
                  <td>${response.office || "-"}</td>
                  <td>${response.department || "-"}</td>
                  <td>${response.rating || "-"}</td>
                  <td>${response.waitingTime || "-"}</td>
                  <td>${response.reasonsRorRating || "-"}</td>
                  <td>${response.gender || "-"}</td>
                  <td>${
                    response.createdAt
                      ? moment(response.createdAt).format("YYYY-MM-DD HH:mm")
                      : "-"
                  }</td>
                </tr>
              `
                )
                .join("")}
            </tbody>
          </table>
        </body>
      </html>
    `;

    printWindow.document.write(htmlContent);
    printWindow.document.close();
    printWindow.focus();

    // Wait for content to load, then print
    setTimeout(() => {
      printWindow.print();
    }, 250);
  };

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (error) {
    return (
      <div className="ussd-page">
        <div className="error-message">
          Error loading USSD data: {(error as Error).message}
        </div>
      </div>
    );
  }

  return (
    <div className="ussd-page">
      <div className="ussd-container">
        <div className="ussd-header">
          <h2>USSD Responses</h2>
          {statsData && (
            <div className="stats-summary">
              <div className="stat-item">
                <span className="stat-label">Total Responses:</span>
                <span className="stat-value">{statsData.data.total}</span>
              </div>
            </div>
          )}
        </div>

        <div className="filters-section">
          <div className="filter-group">
            <label>Date Range:</label>
            <AnalyticsDatePicker
              selectedDate={selectedDate}
              setSelectedDate={setSelectedDate}
              selectedDateFormatted={selectedDateFormatted}
              setSelectedDateFormatted={setSelectedDateFormatted}
              type={dateType}
              setType={setDateType}
              onDateChange={handleDateChange}
            />
          </div>
          <div className="filter-group">
            <label htmlFor="sessionId">Session ID:</label>
            <input
              type="text"
              id="sessionId"
              value={sessionId}
              onChange={(e) => setSessionId(e.target.value)}
              className="text-input"
              placeholder="Search by session ID..."
            />
          </div>
          <div className="filter-group">
            <label htmlFor="pageSize">Rows per page:</label>
            <select
              id="pageSize"
              value={itemsPerPage}
              onChange={(e) => handlePageSizeChange(Number(e.target.value))}
              className="page-size-select"
            >
              <option value={10}>10</option>
              <option value={20}>20</option>
              <option value={50}>50</option>
              <option value={75}>75</option>
              <option value={100}>100</option>
              <option value={500}>500</option>
              <option value={1000}>1000</option>
              <option value={5000}>5000</option>
              <option value={10000}>10000</option>
              <option value={50000}>50000</option>
            </select>
          </div>
          <div className="filter-actions">
            <button
              className="btn btn-secondary btn-sm"
              onClick={handleClearFilters}
            >
              Clear Filters
            </button>
          </div>
          <div className="download-actions">
            <button
              className="btn btn-success btn-sm"
              onClick={downloadCSV}
              disabled={
                !ussdData?.data.responses ||
                ussdData.data.responses.length === 0
              }
            >
              <i className="bx bx-download"></i> CSV
            </button>
            <button
              className="btn btn-success btn-sm"
              onClick={downloadPDF}
              disabled={
                !ussdData?.data.responses ||
                ussdData.data.responses.length === 0
              }
            >
              <i className="bx bx-download"></i> PDF
            </button>
          </div>
        </div>

        <div className="ussd-content">
          {ussdData?.data.responses && ussdData.data.responses.length > 0 ? (
            <div className="table-container">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Session ID</th>
                    <th>Language</th>
                    <th>Institution</th>
                    <th>Office</th>
                    <th>Department</th>
                    <th>Rating</th>
                    <th>Waiting Time</th>
                    <th>Reasons for Rating</th>
                    <th>Gender</th>
                    <th>Date</th>
                  </tr>
                </thead>
                <tbody>
                  {ussdData.data.responses.map(
                    (response: any, index: number) => (
                      <tr key={response.ID || index}>
                        <td>{(currentPage - 1) * itemsPerPage + index + 1}</td>
                        <td className="session-id">
                          {response.sessionId || "-"}
                        </td>
                        <td>{response.language || "-"}</td>
                        <td>{response.institution || "-"}</td>
                        <td>{response.office || "-"}</td>
                        <td>{response.department || "-"}</td>
                        <td>{response.rating || "-"}</td>
                        <td>{response.waitingTime || "-"}</td>
                        <td>{response.reasonsRorRating || "-"}</td>
                        <td>{response.gender || "-"}</td>
                        <td>
                          {response.createdAt
                            ? new Date(response.createdAt).toLocaleString()
                            : "-"}
                        </td>
                      </tr>
                    )
                  )}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="empty-state">
              <p>No USSD responses found.</p>
            </div>
          )}
        </div>

        {ussdData?.data.pagination &&
          ussdData.data.pagination.totalPages > 1 && (
            <div className="ussd-footer">
              <div className="pagination">
                <button
                  className="btn btn-secondary"
                  onClick={() =>
                    setCurrentPage((prev) => Math.max(1, prev - 1))
                  }
                  disabled={currentPage === 1}
                >
                  Previous
                </button>
                <span className="page-info">
                  Page {currentPage} of {ussdData.data.pagination.totalPages}
                </span>
                <button
                  className="btn btn-secondary"
                  onClick={() => setCurrentPage((prev) => prev + 1)}
                  disabled={currentPage >= ussdData.data.pagination.totalPages}
                >
                  Next
                </button>
              </div>
            </div>
          )}
      </div>
    </div>
  );
}

export default UssdPage;
